import React, { useState, useEffect, useCallback } from 'react';
import MPDashboardLayout from '../../components/mp/MPDashboardLayout';
import { platformsApi, mediaApi } from '../../lib/mpApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Loader2, Upload, X, Globe, MessageSquare, Newspaper, Film, Radio as RadioIcon } from 'lucide-react';
import { useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url?.startsWith('/') ? `${BACKEND_URL}${url}` : url;
const CATEGORIES = ['social', 'news', 'regional', 'messaging', 'video', 'custom'];

const categoryMeta = {
  social: { label: 'Social Media', icon: Globe, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
  news: { label: 'News Outlets', icon: Newspaper, color: 'text-red-400 bg-red-500/10 border-red-500/20' },
  regional: { label: 'Regional', icon: Globe, color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  messaging: { label: 'Messaging', icon: MessageSquare, color: 'text-green-400 bg-green-500/10 border-green-500/20' },
  video: { label: 'Video', icon: Film, color: 'text-purple-400 bg-purple-500/10 border-purple-500/20' },
  custom: { label: 'Custom', icon: RadioIcon, color: 'text-muted-foreground bg-muted0/10 border-gray-500/20' },
};

const MPPlatformsPage = () => {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [saving, setSaving] = useState(false);
  const [iconFile, setIconFile] = useState(null);
  const iconInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '', display_name: '', icon: '', color: '#3B82F6',
    category: 'social', region: 'global', custom_icon_url: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await platformsApi.getAll();
      setPlatforms(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load platforms'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = platforms.filter(p => {
    if (search && !p.name?.toLowerCase().includes(search.toLowerCase()) && !p.display_name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCategory !== 'all' && p.category !== filterCategory) return false;
    return true;
  });

  const groupedByCategory = {};
  filtered.forEach(p => {
    const cat = p.category || 'custom';
    if (!groupedByCategory[cat]) groupedByCategory[cat] = [];
    groupedByCategory[cat].push(p);
  });

  const openCreate = () => {
    setEditingPlatform(null);
    setFormData({ name: '', display_name: '', icon: '', color: '#3B82F6', category: 'social', region: 'global', custom_icon_url: '' });
    setIconFile(null);
    setShowDialog(true);
  };

  const openEdit = (p) => {
    setEditingPlatform(p);
    setFormData({
      name: p.name, display_name: p.display_name, icon: p.icon || '',
      color: p.color || '#3B82F6', category: p.category || 'social',
      region: p.region || 'global', custom_icon_url: p.custom_icon_url || '',
    });
    setIconFile(null);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.display_name) { toast.error('Name and display name required'); return; }
    setSaving(true);
    try {
      let iconUrl = formData.custom_icon_url;
      if (iconFile) {
        const result = await mediaApi.upload(iconFile, `${formData.name} icon`);
        iconUrl = result.file_url;
      }
      const payload = { ...formData, custom_icon_url: iconUrl };
      if (editingPlatform) {
        await platformsApi.update(editingPlatform.platform_id, payload);
        toast.success('Platform updated');
      } else {
        await platformsApi.create(payload);
        toast.success('Platform created');
      }
      setShowDialog(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.display_name}"?`)) return;
    try { await platformsApi.delete(p.platform_id); toast.success('Deleted'); loadData(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <MPDashboardLayout title="Platforms">
      <div className="space-y-6" data-testid="mp-platforms-page">
        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search platforms..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-muted border-border text-foreground placeholder:text-muted-foreground" data-testid="mp-platforms-search" />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40 bg-muted border-border text-foreground">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{categoryMeta[c]?.label || c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-red-600 hover:bg-red-700 text-foreground" data-testid="mp-create-platform-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Platform
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {CATEGORIES.map(cat => {
            const meta = categoryMeta[cat];
            const count = platforms.filter(p => (p.category || 'custom') === cat).length;
            return (
              <button key={cat} onClick={() => setFilterCategory(filterCategory === cat ? 'all' : cat)}
                className={`px-3 py-2.5 rounded-lg border text-center transition-all ${
                  filterCategory === cat ? meta.color + ' border-current' : 'bg-card border-border hover:border-border'
                }`}>
                <p className={`text-lg font-bold ${filterCategory === cat ? '' : 'text-foreground'}`}>{count}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{meta.label}</p>
              </button>
            );
          })}
        </div>

        {/* Grouped Platform List */}
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : Object.keys(groupedByCategory).length === 0 ? (
          <div className="text-center py-16 text-muted-foreground"><Globe className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="text-sm">No platforms found</p></div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedByCategory).map(([cat, items]) => {
              const meta = categoryMeta[cat] || categoryMeta.custom;
              const Icon = meta.icon;
              return (
                <div key={cat}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className={`w-4 h-4 ${meta.color.split(' ')[0]}`} />
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground/80">{meta.label}</h2>
                    <Badge variant="secondary" className="text-[10px] bg-muted text-muted-foreground">{items.length}</Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {items.map(p => (
                      <div key={p.platform_id}
                        className="group bg-card border border-border hover:border-border rounded-lg p-4 transition-all"
                        data-testid={`mp-platform-${p.platform_id}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-foreground font-bold flex-shrink-0 shadow-lg"
                            style={{ backgroundColor: p.color || '#666' }}>
                            {p.custom_icon_url ? (
                              <img src={resolveUrl(p.custom_icon_url)} alt="" className="w-full h-full object-contain rounded-lg" />
                            ) : (
                              <span className="text-base">{p.display_name?.charAt(0).toUpperCase()}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-foreground truncate">{p.display_name}</h3>
                            <p className="text-xs text-muted-foreground">@{p.name}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">{p.region || 'global'}</Badge>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground">
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            {!p.is_default && (
                              <Button size="sm" variant="ghost" onClick={() => handleDelete(p)} className="h-7 w-7 p-0 text-muted-foreground hover:text-red-400">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-foreground">{editingPlatform ? 'Edit Platform' : 'Add Platform'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Preview */}
            <div className="flex items-center gap-3 bg-muted rounded-lg p-4 border border-border">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center text-foreground font-bold"
                style={{ backgroundColor: formData.color || '#666' }}>
                {iconFile ? <img src={URL.createObjectURL(iconFile)} alt="" className="w-full h-full object-contain rounded-lg" />
                  : formData.custom_icon_url ? <img src={resolveUrl(formData.custom_icon_url)} alt="" className="w-full h-full object-contain rounded-lg" />
                  : <span className="text-lg">{formData.display_name?.charAt(0)?.toUpperCase() || '?'}</span>}
              </div>
              <div>
                <p className="font-medium text-foreground">{formData.display_name || 'Platform Name'}</p>
                <p className="text-xs text-muted-foreground">@{formData.name || 'slug'} &middot; {formData.region || 'global'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name (slug)</Label>
                <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value.toLowerCase().replace(/\s+/g, '')})}
                  placeholder="twitter" className="bg-muted border-border text-foreground" disabled={!!editingPlatform}
                  data-testid="mp-platform-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Display Name</Label>
                <Input value={formData.display_name} onChange={e => setFormData({...formData, display_name: e.target.value})}
                  placeholder="Twitter/X" className="bg-muted border-border text-foreground"
                  data-testid="mp-platform-display-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
                <Select value={formData.category} onValueChange={v => setFormData({...formData, category: v})}>
                  <SelectTrigger className="bg-muted border-border text-foreground"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{categoryMeta[c]?.label || c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Region</Label>
                <Input value={formData.region} onChange={e => setFormData({...formData, region: e.target.value})}
                  placeholder="global" className="bg-muted border-border text-foreground" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Brand Color</Label>
              <div className="flex gap-3 items-center">
                <input type="color" value={formData.color} onChange={e => setFormData({...formData, color: e.target.value})}
                  className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent" />
                <Input value={formData.color} onChange={e => setFormData({...formData, color: e.target.value})}
                  className="bg-muted border-border text-foreground font-mono" />
                <div className="w-10 h-10 rounded-lg" style={{ backgroundColor: formData.color }} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Custom Icon</Label>
              <div className="flex items-center gap-3">
                {(iconFile || formData.custom_icon_url) && (
                  <div className="relative w-12 h-12 border border-border rounded-lg overflow-hidden">
                    <img src={iconFile ? URL.createObjectURL(iconFile) : resolveUrl(formData.custom_icon_url)} alt="" className="w-full h-full object-contain" />
                    <button onClick={() => { setIconFile(null); setFormData({...formData, custom_icon_url: ''}); }}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-foreground rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
                  </div>
                )}
                <input type="file" ref={iconInputRef} accept="image/*" onChange={e => setIconFile(e.target.files[0])} className="hidden" />
                <Button type="button" variant="outline" onClick={() => iconInputRef.current?.click()}
                  className="border-border text-foreground/80 hover:bg-muted">
                  <Upload className="w-4 h-4 mr-2" /> {formData.custom_icon_url || iconFile ? 'Change' : 'Upload'} Icon
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-border text-foreground/80 hover:bg-muted">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 text-foreground" data-testid="mp-platform-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MPDashboardLayout>
  );
};

export default MPPlatformsPage;
